import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Command, FileText, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { searchDocs, SearchResult } from '../data/searchIndex';

export const DocSearch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Search when query changes
  useEffect(() => {
    if (query.trim().length > 0) {
      const searchResults = searchDocs(query);
      setResults(searchResults);
      setSelectedIndex(0);
    } else {
      setResults([]);
    }
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results.length > 0 && results[selectedIndex]) {
      e.preventDefault();
      handleSelectResult(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSelectResult = (result: SearchResult) => {
    // Close dialog and clear state
    setOpen(false);
    setQuery('');
    setResults([]);

    // Navigate to path first
    navigate(result.path);

    // Then handle the hash/anchor separately
    if (result.anchor) {
      // Wait for navigation to complete, then scroll with retry logic
      const attemptScroll = (attempts = 0) => {
        const element = document.getElementById(result.anchor!);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Also update the URL hash
          window.history.replaceState(null, '', `${result.path}#${result.anchor}`);
        } else if (attempts < 10) {
          setTimeout(() => attemptScroll(attempts + 1), 200);
        }
      };

      setTimeout(() => attemptScroll(), 300);
    }
  };

  // Helper function to highlight search terms in text
  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === searchTerm.toLowerCase()) {
        return (
          <mark
            key={index}
            className='bg-yellow-200 dark:bg-yellow-900/50 text-foreground font-semibold'
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  };

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => setOpen(true)}
        className='flex items-center gap-2 w-full max-w-md px-4 py-2 text-sm text-muted-foreground bg-muted/50 hover:bg-muted rounded-lg border border-border transition-colors'
      >
        <Search className='h-4 w-4' />
        <span>Search documentation...</span>
        <kbd className='ml-auto hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-xs font-medium text-muted-foreground'>
          <span className='flex items-center gap-0.5'>
            <Command className='h-3 w-3' />K
          </span>
          <span className='mx-1'>/</span>
          <span>Ctrl+K</span>
        </kbd>
      </button>

      {/* Search dialog */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className='max-w-2xl p-0 gap-0'>
          <DialogHeader className='px-4 pt-4 pb-0'>
            <DialogTitle className='sr-only'>Search Documentation</DialogTitle>
          </DialogHeader>

          {/* Search input */}
          <div className='flex items-center border-b px-4 pb-4'>
            <Search className='h-5 w-5 text-muted-foreground mr-3' />
            <Input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Search documentation...'
              className='border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base'
            />
          </div>

          {/* Search results */}
          <div className='max-h-[400px] overflow-y-auto p-2'>
            {query.trim().length === 0 ? (
              <div className='py-8 text-center text-sm text-muted-foreground'>
                <FileText className='h-12 w-12 mx-auto mb-3 opacity-50' />
                <p>Type to search documentation</p>
                <p className='text-xs mt-2'>
                  Try searching for "map", "filters", "routing", or "campaigns"
                </p>
              </div>
            ) : results.length === 0 ? (
              <div className='py-8 text-center text-sm text-muted-foreground'>
                <FileText className='h-12 w-12 mx-auto mb-3 opacity-50' />
                <p>No results found for "{query}"</p>
                <p className='text-xs mt-2'>Try different keywords or check your spelling</p>
              </div>
            ) : (
              <div className='space-y-1'>
                {results.map((result, index) => (
                  <button
                    key={result.path}
                    onClick={() => handleSelectResult(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      index === selectedIndex
                        ? 'bg-primary/10 border border-primary/20'
                        : 'hover:bg-muted border border-transparent'
                    }`}
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 mb-1'>
                          <span className='text-xs font-medium text-muted-foreground px-2 py-0.5 bg-muted rounded'>
                            {result.category}
                          </span>
                          <h4 className='text-sm font-semibold text-foreground truncate'>
                            {result.title}
                          </h4>
                        </div>
                        {result.matchedSection && (
                          <div className='text-xs text-primary font-medium mb-1'>
                            → {result.matchedSection}
                          </div>
                        )}
                        <p className='text-xs text-muted-foreground line-clamp-2'>
                          {result.snippet
                            ? highlightMatch(result.snippet, query)
                            : result.description}
                        </p>
                      </div>
                      <ArrowRight
                        className={`h-4 w-4 flex-shrink-0 mt-1 transition-opacity ${
                          index === selectedIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer with keyboard hints */}
          {results.length > 0 && (
            <div className='border-t px-4 py-2 flex items-center justify-between text-xs text-muted-foreground bg-muted/30'>
              <div className='flex items-center gap-4'>
                <span className='flex items-center gap-1'>
                  <kbd className='px-1.5 py-0.5 bg-background border rounded'>↑</kbd>
                  <kbd className='px-1.5 py-0.5 bg-background border rounded'>↓</kbd>
                  Navigate
                </span>
                <span className='flex items-center gap-1'>
                  <kbd className='px-1.5 py-0.5 bg-background border rounded'>Enter</kbd>
                  Select
                </span>
                <span className='flex items-center gap-1'>
                  <kbd className='px-1.5 py-0.5 bg-background border rounded'>Esc</kbd>
                  Close
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
